import { IconButton, SingleSelectDropdown, Text } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import styles from "./PaginationFooter.module.css";

// 행 수 선택지다. 오름차순이므로 첫 값이 가장 작다. 이 값이 한 페이지 판정의 기준이다.
const pageSizeChoices = [20, 50, 100] as const;
const pageSizeOptions = pageSizeChoices.map((choice) => ({
  value: String(choice),
  label: String(choice),
}));

export type PaginationFooterProps = {
  page: number;
  size: number;
  total: number;
  isLoading: boolean;
  // 화면마다 고유한 페이지 이동 이름이다(예: "리뷰 대기 목록 페이지 이동"). 필수 값이므로
  // 화면이 다른 화면의 라벨을 그대로 쓸 수 없다.
  ariaLabel: string;
  // 표시 mode 다.
  //   - surface: 테두리 스트립 + 페이지 pill(데이터셋 계열).
  //   - compact: 한 줄 우측 정렬(세션 계열).
  mode?: "surface" | "compact";
  // 한 페이지면 footer 를 그리지 않는다. 단 trailingAction 이 있으면 액션은 숨기지 않는다.
  // 판정 기준은 onPageSizeChange 유무에 따라 다르다. 아래 singlePageSize 를 참고한다.
  hideWhenSinglePage?: boolean;
  // 다음 버튼 뒤에 붙는 부가 액션이다(예: 컬럼 메뉴).
  trailingAction?: ReactNode;
  // Surface mode 좌측 슬롯이다(예: 선택 액션). 총합·범위는 화면 상단 요약이 이미 표시한다.
  // 그래서 이 위치에 문구를 넣지 않는다. Compact mode 는 자체 범위 라벨을 유지한다.
  leadingAction?: ReactNode;
  // 이 값을 주면 행 수 선택 컨트롤이 footer 맨 앞에 추가되고, 고른 값을 보고한다. 크기는 소스를
  // 소유한 쪽이 적용한다. 그래서 이 컴포넌트는 값을 보관하지 않는다.
  onPageSizeChange?: (pageSize: number) => void;
  onPrevious: () => void;
  onNext: () => void;
};

// prev/next 페이지네이션 footer 의 공용 구현이다. first/last 항목, 총 페이지, 버튼 비활성 계산을
// 소유한다. total 이 0 이면 1/1 로 표시하고 이전/다음을 모두 비활성으로 고정한다.
// Surface mode 는 총합·범위 문구를 두지 않는다. 화면 상단 요약이 이미 표시한다.
// 좌측은 leadingAction 슬롯이다.
export function PaginationFooter({
  page,
  size,
  total,
  isLoading,
  ariaLabel,
  mode = "surface",
  hideWhenSinglePage = false,
  trailingAction = null,
  leadingAction = null,
  onPageSizeChange,
  onPrevious,
  onNext,
}: PaginationFooterProps): ReactNode {
  const totalPages = Math.max(1, Math.ceil(total / size));
  // 행 수를 고를 수 있으면 "한 페이지"는 가장 작은 선택지로 판정한다. 큰 size 를 골라 한 페이지가
  // 됐을 때 footer 를 숨기면, 크기를 되돌릴 컨트롤도 함께 사라진다.
  const singlePageSize = onPageSizeChange != null ? pageSizeChoices[0] : size;
  if (hideWhenSinglePage && Math.ceil(total / singlePageSize) <= 1 && trailingAction == null) {
    return null;
  }

  const pageSizeControl =
    onPageSizeChange != null ? (
      <SingleSelectDropdown
        mode={hatcheryDropdownModes.default}
        label="Rows"
        size="sm"
        value={String(size)}
        options={pageSizeOptions}
        onValueChange={(next) => onPageSizeChange(Number(next))}
      />
    ) : null;

  const firstItem = total === 0 ? 0 : (page - 1) * size + 1;
  const lastItem = Math.min(page * size, total);
  const previousDisabled = isLoading || page <= 1;
  const nextDisabled = isLoading || page >= totalPages;

  if (mode === "compact") {
    return (
      <div className={styles.compactFooter} aria-label={ariaLabel}>
        {pageSizeControl}
        <Text variant={hatcheryTextModes["caption-normal"]} className={styles.compactRange}>
          {total === 0 ? "총 0개" : `${firstItem}–${lastItem} / ${total}`}
        </Text>
        <IconButton
          text="이전"
          size="sm"
          variant="surface"
          disabled={previousDisabled}
          onClick={onPrevious}
        />
        <IconButton
          text="다음"
          size="sm"
          variant="surface"
          disabled={nextDisabled}
          onClick={onNext}
        />
        {trailingAction}
      </div>
    );
  }

  return (
    <div className={styles.surfaceFooter} aria-label={ariaLabel}>
      {pageSizeControl}
      {leadingAction}
      <div className={styles.actions}>
        <IconButton
          text="이전"
          size="sm"
          variant="surface"
          disabled={previousDisabled}
          onClick={onPrevious}
        />
        <Text variant={hatcheryTextModes["caption-bold"]} className={styles.pageStatus}>
          {page} / {totalPages}
        </Text>
        <IconButton
          text="다음"
          size="sm"
          variant="surface"
          disabled={nextDisabled}
          onClick={onNext}
        />
        {trailingAction}
      </div>
    </div>
  );
}
